import { Link } from "react-router-dom";
import { Phone, MapPin, ArrowRight } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { CtaBand } from "../components/CtaBand";
import { SITE } from "../data/site";

const NOVA_WHO = [
  {
    title: "Families after a loss",
    text: "An Arlington colonial or an Alexandria townhome full of a loved one's belongings is hard to face from the kitchen table, harder still from another state. We take the logistics off your plate.",
  },
  {
    title: "Longtime owners trading down",
    text: "Leaving a Vienna or Fairfax home after twenty or thirty years means impossible decisions. We help you sort with patience, sell what's worth selling, and donate the rest.",
  },
  {
    title: "Executors & estate attorneys",
    text: "Probate timelines don't wait. We give Northern Virginia estate professionals a dependable local team for documented sales and timely cleanouts.",
  },
  {
    title: "Realtors with a listing to prep",
    text: "From McLean estates to Springfield ramblers, we declutter and empty homes so they photograph and show at their best.",
  },
];

const NOVA_SERVICES = [
  { name: "Estate Sales", to: "/services/estate-sales", note: "Well-run estate sales in Northern Virginia — staged in the home, priced fairly, and promoted to serious buyers." },
  { name: "Estate Cleanouts", to: "/services/cleanouts-downsizing", note: "Whole-house cleanouts across Northern Virginia, from townhomes to multi-level colonials." },
  { name: "Downsizing", to: "/services/cleanouts-downsizing", note: "Downsizing services in Northern Virginia for moves to condos, senior communities, or closer to grandkids." },
  { name: "Packing & Moving Assistance", to: "/services", note: "Careful packing and labeling, move-day coordination, and settling-in help at the new place." },
  { name: "Donation Pickup & Furniture Removal", to: "/services", note: "Paid pickup and removal of unwanted furniture and household items for a service fee — donatable goods go to local charities, not landfills." },
  { name: "Decluttering & Home Organization", to: "/services", note: "Pre-listing declutters and room-by-room resets that make daily life easier." },
  { name: "Junk & Unwanted-Item Removal", to: "/services", note: "From a single sofa to a full property — hauled away responsibly." },
  { name: "Hoarding Assistance", to: "/services", note: "Quiet, judgment-free help that moves at a pace your family member can live with." },
];

const NOVA_COMMUNITIES = [
  "Arlington", "Alexandria", "Falls Church", "McLean", "Fairfax",
  "Annandale", "Springfield", "Vienna", "Reston", "Herndon",
];

export default function NorthernVirginiaPage() {
  usePageMeta(
    "Estate Sales & Cleanouts in Northern Virginia | MacWay Estate Services",
    "Estate sales, estate cleanouts, and downsizing services in Northern Virginia — Arlington, Alexandria, Falls Church, McLean, Fairfax, Reston, and nearby communities. Call (301) 500-0128."
  );

  return (
    <main data-testid="northern-virginia-page">
      <section className="bg-forest text-ivory py-20 md:py-28" data-testid="nova-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <p className="eyebrow-label">Service area · Northern Virginia</p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-semibold tracking-tight leading-[1.08]" data-testid="nova-headline">
            Careful estate help for Northern Virginia homes
          </h1>
          <p className="mt-6 text-lg text-ivory/85 leading-relaxed max-w-2xl">
            Northern Virginia moves fast — estates, relocations, and downsizing decisions often arrive on a
            deadline. MacWay brings a calm, capable team to the house itself: estate sales, estate cleanouts,
            downsizing help, packing and moving assistance, donation pickups, decluttering, junk removal, and
            respectful hoarding assistance.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="btn-brass" data-testid="nova-quote-cta">Request a Quote</Link>
            <a href={SITE.phoneHref} className="btn-outline-light" data-testid="nova-call-cta">
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory" data-testid="nova-who">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">Who we help</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              Northern Virginia families and the professionals behind them
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {NOVA_WHO.map((w) => (
              <div key={w.title} className="bg-white border border-linen p-8">
                <h3 className="text-2xl font-medium text-ink">{w.title}</h3>
                <p className="mt-3 text-base text-ink/70 leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-sand" data-testid="nova-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">Our services in Northern Virginia</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              One team for the whole house
            </h2>
            <p className="mt-5 text-base sm:text-lg text-ink/70 leading-relaxed">
              Whether it's a sale, a cleanout, or a careful downsizing, every project starts the same way: a
              walkthrough of the property and a clear written quote.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {NOVA_SERVICES.map((s) => (
              <Link
                key={s.name}
                to={s.to}
                className="group bg-white border border-linen p-7 hover:border-forest/40 hover:shadow-md transition-all duration-300"
                data-testid={`nova-service-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-xl font-medium text-ink">{s.name}</h3>
                  <ArrowRight className="w-4 h-4 mt-1.5 text-brass shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
                <p className="mt-2.5 text-sm text-ink/65 leading-relaxed">{s.note}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory" data-testid="nova-areas">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-forest text-ivory p-10 md:p-14">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
              <div>
                <p className="eyebrow-label">Where we work</p>
                <h2 className="mt-4 text-3xl sm:text-4xl font-medium tracking-tight">
                  Northern Virginia communities we serve
                </h2>
                <p className="mt-4 text-base text-ivory/75 leading-relaxed">
                  We work throughout Northern Virginia's close-in communities and their neighbors. Not sure your
                  town is in range? A quick call to{" "}
                  <a href={SITE.phoneHref} className="font-semibold text-ivory underline decoration-brass underline-offset-4 hover:text-brass transition-colors">{SITE.phone}</a>{" "}
                  settles it.
                </p>
              </div>
              <div className="lg:col-span-2">
                <div className="flex flex-wrap gap-2.5">
                  {NOVA_COMMUNITIES.map((c) => (
                    <span key={c} className="inline-flex items-center gap-2 bg-ivory/10 border border-ivory/25 px-4 py-2 text-sm font-semibold" data-testid={`nova-community-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <MapPin className="w-3.5 h-3.5 text-brass" /> {c}
                    </span>
                  ))}
                  <span className="px-4 py-2 text-sm text-ivory/60 italic">…and nearby Northern Virginia communities</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBand
        title="Let's talk about the Northern Virginia property."
        text="A short conversation about the house and your timeline is all it takes to get started — followed by a walkthrough and a clear written quote."
      />
    </main>
  );
}
